import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import useUserData from '../../hooks/useUserData';

const Navigation = () => {
    const cartLength = useSelector((state) => state.cart.cartLength);
    const {userData:user} = useUserData();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [scrolled, setScrolled] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const navRef = useRef(null);

    const logoImg = '/logo.jpg';

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setMobileOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSearch = () => {
        if (searchKeyword.trim()) {
            setMobileOpen(false);
            navigate(`/products/?search=${encodeURIComponent(searchKeyword)}`);
        } else {
            message.error('Enter a search keyword');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const isActive = (path) => location.pathname === path;

    return (
        <nav className={`nav-modern ${scrolled ? 'scrolled' : ''}`} ref={navRef}>
            <div className={`nav-inner ${mobileOpen ? 'mobile-open' : ''}`}>
                <Link className="nav-logo" to="/">
                    <img src={logoImg} alt="A Green Beginning" />
                    <div className="nav-logo-text">
                        <span className="brand">A Green Beginning</span>
                        <span className="tagline">Plants & Seeds</span>
                    </div>
                </Link>

                <div className="nav-search">
                    <input
                        className="nav-search-input"
                        type="search"
                        placeholder="Search plants, seeds..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button className="nav-search-btn" onClick={handleSearch}>
                        <i className="fas fa-search"></i>
                    </button>
                </div>

                <div className="nav-links">
                    <Link
                        className={`nav-link-item ${isActive('/products') ? 'active' : ''}`}
                        to="/products"
                    >
                        <i className="fas fa-leaf"></i>
                        <span>Shop</span>
                    </Link>
                    <Link
                        className={`nav-link-item ${isActive('/contact-us') ? 'active' : ''}`}
                        to="/contact-us"
                    >
                        <i className="fas fa-envelope"></i>
                        <span>Contact</span>
                    </Link>
                    <Link
                        className={`nav-link-item ${isActive('/profile') || isActive('/login') ? 'active' : ''}`}
                        to={user ? '/profile' : '/login'}
                    >
                        <i className="fas fa-user"></i>
                        <span>{user ? 'Profile' : 'Login'}</span>
                    </Link>
                    <Link
                        className={`nav-link-item nav-cart ${isActive('/cart') ? 'active' : ''}`}
                        to="/cart"
                    >
                        <i className="fas fa-shopping-cart"></i>
                        <span>Cart</span>
                        {(cartLength ?? 0) > 0 && (
                            <span className="nav-cart-badge">{cartLength}</span>
                        )}
                    </Link>
                </div>

                <button
                    className="nav-mobile-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        setMobileOpen(!mobileOpen);
                    }}
                >
                    <i className={`fas ${mobileOpen ? 'fa-times' : 'fa-bars'}`}></i>
                </button>
            </div>
        </nav>
    );
};

export default React.memo(Navigation);
